import { Suspense, useState, useEffect, useMemo } from "react";
import LocaleContext from "./context/LocaleContext";
import { WelcomeBackProvider } from "./context/WelcomeBackProvider";
import Routes from "./routes";
import i18n from "./utils/i18n";
import { useDispatch, useSelector } from "react-redux";
import {
	setUserCurrentLocation,
	setUserEntity,
	removeForgotPass,
	languagequeryParam,
	loginResponse,
	autentication,
	storelangList,
	setAllEnLabels
} from "./reducers/signUpSlice";
import { configurationServices } from "./constants/configurationServices";
import Loader from "./components/Loader";
import { currentCountrySave, ipAddressSave } from "./services/ipAddress.service";
import { store } from "./store";
import { getUserDetails } from "./utils/getUsersDetails";
import { getHrinTriggerValue } from "./utils/helpers";
import { updateUserInfo } from "./reducers/userInfoSlice";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { countryList, updateCspNonce } from "./reducers/commonSlice";
import { getGeoLocationEndpoint } from "./utils/endPointMapper";
import useManageCookieBtn from './hooks/useManageCookieBtn';
import Cookies from 'js-cookie';
import "./App.scss";
import DOMPurify from 'dompurify';

const geolocationAPI = getGeoLocationEndpoint();

/* istanbul ignore next */
function App() {
	const dispatch = useDispatch();
	const { languageParam, getUserLocation, userEntity } = useSelector((state) => state.signUp);
	const state = store.getState();
	const [locale, setLocale] = useState(languageParam ? languageParam : "en-US");
	const languageVal = languageParam ? languageParam : locale;

	const nonce = document.getElementById("root")?.getAttribute("data-cspValue");
	const memoizedContextValue = useMemo(() => ({ locale, languageVal, setLocale }), [locale, languageVal])
	const loadDynamicScript = () => {
		if (typeof window.__fxcmGtm === 'undefined' && document.getElementById('gtm') === null) {
			const useSelfHostedGA = process.env.REACT_APP_SELF_HOSTED_GA;
			const gtmKey = process.env.REACT_APP_ANALYTICS_MEASUREMENT_ID;
			const url =
				useSelfHostedGA && useSelfHostedGA === "true"
					? window.location.origin + "/tagmanager"
					: "https://www.googletagmanager.com";
			const script = document.createElement("script");
			script.type = "text/javascript";
			script.setAttribute('id', 'gtm');
			script.setAttribute('nonce', nonce);
			script.textContent = `
					(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
					new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
					j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
					'${url}/gtm.js?id='+i+dl;j.setAttribute('nonce','${nonce}');
					f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmKey}');
				`;
			document.head.appendChild(script);
		}
	};

	useEffect(() => {
		if (nonce && window.location.pathname !== "/health") {
			loadDynamicScript();
			dispatch(updateCspNonce(nonce));
			// Removing nonce from the data attribute for security reason.
			const nonceValue = document.getElementById('root');
			nonceValue.removeAttribute("data-cspValue");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nonce]);

	useEffect(() => {
		if (userEntity) {
			localStorage.setItem("SELECTED_ENTITY", userEntity);
			i18n.reloadResources("en-US", "language").then(() => {
				const labels = i18n.getResourceBundle("en-US", "language");
				if (labels) {
					dispatch(setAllEnLabels(labels));
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userEntity]);

	useManageCookieBtn();

	const fetchUserDetails = async () => {
		const response = await getUserDetails(Cookies?.get("token"), "refresh");
		const responseData = response?.data?.["user-details"];
		const pkaAllInfo = responseData?.pkainfo  ? JSON.parse(responseData.pkainfo)  : undefined;
		let accRejectionStatus = "SUCCESS";
		if (responseData?.userForms?.currentStep === "ONBOARDING_COMPLETE") {
			if (responseData?.userForms?.accountRejectionReason)
				accRejectionStatus = responseData?.userForms?.accountRejectionReason;
		}
		const payLoadData = {
			...responseData?.userForms,
			fxowsAccountNumber: responseData?.fxowsAccountNumber,
			email: responseData?.email,
			userName: responseData?.username,
			countryOfResidence: responseData?.countryOfResidenceCode,
			locale: responseData?.userLocale,
			authToken: Cookies?.get("token"),
			currentStep: responseData?.userForms.currentStep || "SIGN_UP",
			accountRejectionReason: accRejectionStatus,
			pkaRetake: responseData?.entity === "FXCM_UK" && responseData?.pkaRemainingAttempts ? responseData?.pkaRemainingAttempts?.toString() : responseData?.pkainfo,
			pkaAllInfo: pkaAllInfo,
			hrinTrigger: getHrinTriggerValue(responseData),
			applicationStatus: responseData?.userForms?.applicationStatus,
			crsStatus: responseData?.userForms?.crsStatus,
			denomination: responseData?.denomination,
			platform: responseData?.platform,
			successPage: responseData?.userForms?.currentStep === "ONBOARDING_COMPLETE" ? true : false
		}
		let signUpPayload = {}
		if (responseData?.rbName) {
			signUpPayload = {
				authRes: true,
				authToken: Cookies?.get("token"),
				rb_platformOptions: responseData?.rbPlatformOption ? responseData?.rbPlatformOption : [],
				rb_defaultPlatform: responseData?.rbDefaultPlatform ? responseData?.rbDefaultPlatform : '',
				rb_denominationOptions: responseData?.rbDenominationOptions ? responseData?.rbDenominationOptions : []
			}
		} else {
			signUpPayload = {
				// ...data12.data?.["user-details"],
				authRes: true,
				authToken: Cookies?.get("token")
			}
		}
		const responsePayload = {
			...response,
			authRes: response?.success
		}
		dispatch(setUserEntity(responseData?.entity));
		dispatch(updateUserInfo(payLoadData));
		dispatch(loginResponse(responsePayload));
		dispatch(autentication(signUpPayload));
	};

	const fetchAPIResponse = async (url) => {
		try {
			const data = await fetch(url);
			if (!data.ok) {
				console.error("Error fetching API Data");
				return null;
			}
			return data.json();
		} catch (err) {
			console.error("Fetch failed:", err);
			return null;
		}
	}

	const getGeoLocation = () => {
		const isEmptyLocation = Object.values(state.ipAddress).includes(null);
		if (isEmptyLocation) {
			return fetchAPIResponse(geolocationAPI);
		}
		return null;
	}

	const fetchCountryJson = () => {
		if (!state.signUp.authRes) {
			return fetchAPIResponse(configurationServices().countryList);
		}
		return null;
	};

	const fetchEntityLangList = () => fetchAPIResponse(configurationServices().entityLanguageListURL);

	useEffect(() => {
		const fetchAllData = async () => {
			const [countryData, entityLangData, geoLocationData] = await Promise.all([
				fetchCountryJson(),
				fetchEntityLangList(),
				getGeoLocation()
			]);
			// Country List data update
			if (countryData) {
				dispatch(countryList(countryData));
			}
			// entityLanguagelist data update
			if (entityLangData) {
				dispatch(storelangList(entityLangData));
			}
			// GeoLocation data update
			if (geoLocationData) {
				if (countryData) {
					const geoCountry = geoLocationData.country.toUpperCase();
					const currentCOR = countryData?.find((item) => item.country_desc === geoCountry);
					dispatch(setUserCurrentLocation(currentCOR.country_code));
					dispatch(currentCountrySave(currentCOR.country_code)); // storing current country of user
				}
				const uniqueIp = Array.from(new Set(geoLocationData.ipAddress?.split(",")))[0];
				dispatch(ipAddressSave(uniqueIp));
			}
		};
		fetchAllData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	/*
	To reset to initial state and logout if the user refreshes or 
	if user is ideal and session timeout happens
	*/
	useEffect(() => {
		// Fetch user Details on refresh
		if (Cookies?.get('token')) {
			fetchUserDetails();
		}
		dispatch(removeForgotPass());

		let selectedCountry = DOMPurify.sanitize(sessionStorage.getItem('PRE_SELECTED_COUNTRY') || "");
		if (selectedCountry !== null && selectedCountry !== '') {
			sessionStorage.setItem("PRE_SELECTED_COUNTRY", getUserLocation);
			window.addEventListener("beforeunload", preSelectCountryName);
		}
		return () => {
			window.removeEventListener("beforeunload", preSelectCountryName);
		};
	}, []);

	const preSelectCountryName = (e) => {
		if ((window.location.pathname !== "/" && window.location.pathname !== "/login")) {
			sessionStorage.setItem("PRE_SELECTED_COUNTRY", "");
		}
	};

	useEffect(() => {
		i18n.on("languageChanged", (lng) => {
			setLocale(lng);
			if(!localStorage.getItem("PRE_SELECTED_LANG")) {
				localStorage.setItem("PRE_SELECTED_LANG", lng);
			}
			dispatch(languagequeryParam(lng));
		});

		return () => {
			i18n.off("languageChanged");
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, languageParam]);

	useEffect(() => {
		if (!userEntity && getUserLocation) {
			let countriesListURL = configurationServices(userEntity).countryList;
			fetch(countriesListURL)
				.then((response) => response.json())
				.then((data) => {
					const country = data.find(item => item.country_code === getUserLocation);
					if (country?.entity_code) {
						dispatch(setUserEntity(country.entity_code));
					} else {
						dispatch(setUserEntity("FXCM_EU"));
					}
				})
				.catch((err) => {
					console.error("Entity resolution failed:", err);
					dispatch(setUserEntity("FXCM_EU"));
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getUserLocation, userEntity, dispatch]);

	// Create rtl cache
	const cacheRtl = createCache({
		key: "muirtl",
		stylisPlugins: [prefixer, rtlPlugin],
	});

	let route;
	if (locale === "ar-AE") {
		route = (
			<CacheProvider value={cacheRtl}>
				<Routes />
			</CacheProvider>
		);
	} else {
		route = <Routes />;
	}

	if (!getUserLocation || !userEntity) {
		return <Loader />;
	}

	return (
		<LocaleContext.Provider value={memoizedContextValue}>
			<WelcomeBackProvider>
				<Suspense fallback={<Loader />}>
					<HelmetProvider>
						<Helmet>
							{languageVal === "ar-AE"
								? document.body.setAttribute("dir", "rtl")
								: document.body.setAttribute("dir", "ltr")}
						</Helmet>
					</HelmetProvider>
					<div className="App">{route}</div>
				</Suspense>
			</WelcomeBackProvider>
		</LocaleContext.Provider>
	);
}

export default App;
